import { MatDialog } from '@angular/material/dialog';
import { Injectable } from '@angular/core';
import { ApiService } from 'app/shared/apis/api.service';
import { BaseService } from 'app/core/services/base.service';
import { UserModel } from 'app/models/user.model';
import { IFilterPagination } from 'app/shared/interfaces/filter-interface';
import { IPagination } from 'app/shared/interfaces/pagination.dto';
import { LoyaltyPointMovement } from 'app/models/loyalty-point-movement.model';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class UserService extends BaseService<UserModel> {
    constructor(api: ApiService, _matDialog: MatDialog) {
        super(api, _matDialog, 'user');
    }

    /** Lista usuários incluindo soft-deleted (ex.: filtro por vendedor removido). */
    listIncludingDeleted(filter?: IFilterPagination<UserModel>, reqOpts?: { headers?: { [name: string]: string } }) {
        const $request = this.api.getSub<IPagination<UserModel>>(`${this.endpoint}/list/deleted`, filter, reqOpts);
        return this.withPromise($request);
    }

    /** Vendedores ativos e inativos para dropdowns de filtro (vendas, financeiro, etc.). */
    listSellersForFilter(options?: { company_id?: number | null; limit?: number | 'all' }) {
        const params: IFilterPagination<UserModel> & { sellers_for_filter?: boolean } = {
            limit: options?.limit ?? 'all',
            sellers_for_filter: true,
            sort: 'name',
            direction: 'ASC',
        };
        if (options?.company_id != null) {
            params.company_id = options.company_id;
        }
        return this.listIncludingDeleted(params);
    }

    /** Usuários internos para a tela de administração (sem soft-deleted). */
    listInternalUsers(filter?: IFilterPagination<UserModel> & { internal_users_only?: boolean }, reqOpts?: { headers?: { [name: string]: string } }) {
        const params = {
            internal_users_only: true,
            sort: 'name',
            direction: 'ASC',
            ...filter,
        };
        return this.list(params, reqOpts);
    }

    restore(user_id: number) {
        return this.api.putSub<UserModel>(`${this.endpoint}/${user_id}/restore`, {});
    }

    restoreByDoc(doc_number: string) {
        const clean = String(doc_number || '').replace(/\D/g, '');
        return this.api.putSub<UserModel>(`${this.endpoint}/restore-by-doc`, { doc_number: clean });
    }

    bulkClearSeller(user_ids: number[]) {
        return this.api.postSub<{ updated: number }>('user/bulk-clear-seller', { user_ids });
    }

    loyaltyPointMovements(
        user_id: number,
        params: { company_id: number; page?: number; limit?: number },
    ): Observable<IPagination<LoyaltyPointMovement>> {
        return this.api.getSub<IPagination<LoyaltyPointMovement>>(
            `${this.endpoint}/${user_id}/loyalty-point-movements`,
            params,
        ) as Observable<IPagination<LoyaltyPointMovement>>;
    }

    loyaltyPointsDebit(
        user_id: number,
        body: { company_id: number; lines: { sale_id: number; points: number }[] },
    ): Observable<{ ok: boolean }> {
        return this.api.postSub<{ ok: boolean }, typeof body>(`${this.endpoint}/${user_id}/loyalty-points/debit`, body);
    }

    updateLoyaltyPointMovement(
        user_id: number,
        movement_id: number,
        body: { company_id: number; lines: { sale_id: number; points: number }[] },
    ): Observable<{ ok: boolean }> {
        return this.api.putSub<{ ok: boolean }>(`${this.endpoint}/${user_id}/loyalty-point-movements/${movement_id}`, body);
    }

    deleteLoyaltyPointMovement(user_id: number, movement_id: number, company_id: number): Observable<{ ok: boolean }> {
        const q = `company_id=${encodeURIComponent(String(company_id))}`;
        return this.api.deleteSub<{ ok: boolean }>(
            `${this.endpoint}/${user_id}/loyalty-point-movements/${movement_id}?${q}`,
        );
    }
}
